import React, { useState } from 'react'
import {
  Brain, ChevronRight, CheckCircle, XCircle,
  RotateCcw, MessageCircle, Target,
  AlertTriangle, BookOpen, Sparkles, ChevronDown, ChevronUp
} from 'lucide-react'
import { useUnitProgress } from '../store/progressStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'G1-1'

interface TutorScenario {
  id: string
  label: string
  emoji: string
  category: string
  learnStepId: string
  question: string
  correctAnswer: string
  wrongAnswer: string
  misconceptionCode: string
  note: string
  analysis: {
    diagnosis: string
    rootCause: string
    correction: string
    example: string
  }
  followUps: Array<{ q: string; a: string }>
}

const DEMO_SCENARIOS: TutorScenario[] = [
  {
    id: 'S1',
    label: '시나리오 1',
    emoji: '📐',
    category: '점·선·면',
    learnStepId: 'GA01',
    question: '직선, 반직선, 선분의 차이를 설명하면?',
    correctAnswer: '직선: 양쪽으로 무한히 뻗음 / 반직선: 한쪽만 무한 / 선분: 양 끝점 사이',
    wrongAnswer: '직선과 선분은 같다 (둘 다 두 점을 연결하니까)',
    misconceptionCode: 'GE01',
    note: '직선·반직선·선분의 개념 혼동',
    analysis: {
      diagnosis: '학생이 "직선과 선분은 같다"고 답했습니다. 두 점을 지난다는 공통점만 보고 뻗음의 방향·범위 차이를 놓쳤습니다.',
      rootCause: '직선은 두 점을 지나 양쪽으로 무한히 연장되지만, 선분은 두 끝점 사이만, 반직선은 한 점에서 한쪽으로만 뻗습니다.',
      correction: '세 가지 비교:\n① 직선 AB: ←●━━━●→ (양방향 무한)\n② 반직선 AB: ●━━━→ (A에서 B방향 무한)\n③ 선분 AB: ●━━━● (A와 B 사이만)',
      example: '교실 벽: 선분 (유한)\n철로 (멀리): 직선 (무한으로 연장)\n손전등 빛: 반직선 (한쪽으로만)',
    },
    followUps: [
      { q: '두 점 A, B를 지나는 직선은 몇 개?', a: '오직 1개! 두 점을 지나는 직선은 유일합니다.' },
      { q: '선분 AB와 선분 BA는 같은가요?', a: '같습니다! 선분은 방향이 없으므로 AB=BA입니다.' },
    ],
  },
  {
    id: 'S2',
    label: '시나리오 2',
    emoji: '📏',
    category: '각의 종류',
    learnStepId: 'GA02',
    question: '135°는 어떤 각인가요?',
    correctAnswer: '둔각 (90°보다 크고 180°보다 작으므로)',
    wrongAnswer: '직각 (90°에 가깝게 보여서)',
    misconceptionCode: 'GE02',
    note: '각도 범위에 따른 각의 종류 혼동',
    analysis: {
      diagnosis: '학생이 135°를 직각이라고 했습니다. 직각은 정확히 90°인데, 135°는 90°를 초과합니다.',
      rootCause: '각의 종류는 각도 범위로 구분합니다. 0°~90° 미만: 예각 / 90°: 직각 / 90°~180° 미만: 둔각 / 180°: 평각',
      correction: '각의 분류표:\n• 예각: 0° < a < 90°\n• 직각: a = 90°\n• 둔각: 90° < a < 180°\n• 평각: a = 180°\n\n135°는 90° < 135° < 180° → 둔각 ✓',
      example: '30° → 예각\n90° → 직각\n120° → 둔각\n180° → 평각',
    },
    followUps: [
      { q: '예각은 몇 도 미만인가요?', a: '90° 미만 (단, 0°보다는 커야 합니다).' },
      { q: '둔각삼각형에는 둔각이 몇 개 있을 수 있나요?', a: '오직 1개입니다. 세 각의 합이 180°이므로 둔각이 2개이면 합이 180°를 초과합니다.' },
    ],
  },
  {
    id: 'S3',
    label: '시나리오 3',
    emoji: '🔀',
    category: '평행선과 각',
    learnStepId: 'GA03',
    question: '두 직선이 평행할 때, 동위각과 엇각의 관계는?',
    correctAnswer: '동위각은 서로 같고, 엇각도 서로 같다',
    wrongAnswer: '동위각은 같고, 엇각은 합이 180°이다',
    misconceptionCode: 'GE03',
    note: '동위각과 엇각의 성질 혼동 (엇각을 보각으로 착각)',
    analysis: {
      diagnosis: '학생이 엇각의 합이 180°라고 답했습니다. 엇각은 합이 아닌 "서로 같은" 관계입니다.',
      rootCause: '평행선에서 동위각(같은 위치의 각)과 엇각(엇갈린 위치의 각)은 모두 크기가 같습니다. 합이 180°가 되는 것은 동측 내각(같은 쪽 내각)입니다.',
      correction: '평행선과 두 직선:\n• 동위각: 크기가 같다 (∠a = ∠b)\n• 엇각: 크기가 같다 (∠c = ∠d)\n• 동측 내각: 합이 180° (∠e + ∠f = 180°)',
      example: '도로의 횡단보도 선과 중앙선:\n평행한 두 선을 하나의 선이 가로지를 때\n같은 쪽 각도(동위각)는 항상 같습니다.',
    },
    followUps: [
      { q: '두 직선이 평행하지 않으면 동위각도 같나요?', a: '아닙니다! 동위각이 같을 때 두 직선이 평행하고, 역도 성립합니다.' },
      { q: '엇각이 같다는 것을 어떻게 기억하나요?', a: 'Z자 모양으로 생각하세요! Z자의 위아래 꺾인 각이 엇각이고 크기가 같습니다.' },
    ],
  },
  {
    id: 'S4',
    label: '시나리오 4',
    emoji: '↕️',
    category: '두 직선의 위치 관계',
    learnStepId: 'GA04',
    question: '공간에서 두 직선이 만나지도 않고 평행하지도 않을 수 있나요?',
    correctAnswer: '예! 꼬인 위치라고 합니다 (공간에서만 존재)',
    wrongAnswer: '아니요. 만나지 않으면 반드시 평행합니다',
    misconceptionCode: 'GE04',
    note: '평면 개념을 공간에 잘못 적용 (꼬인 위치 개념 누락)',
    analysis: {
      diagnosis: '학생이 "만나지 않으면 평행"이라고 했습니다. 이는 평면에서만 옳습니다. 공간에서는 꼬인 위치가 존재합니다.',
      rootCause: '평면에서는 두 직선 관계가 ①한 점에서 만남 ②평행(만나지 않음) 2가지이지만, 공간에서는 ③꼬인 위치가 추가됩니다.',
      correction: '공간에서 두 직선의 위치 관계:\n① 한 점에서 만남\n② 평행 (같은 평면 위, 만나지 않음)\n③ 꼬인 위치 (같은 평면 위에 없고, 만나지도 않음)\n\n예: 직육면체의 모서리 AB와 CD는 꼬인 위치',
      example: '직육면체에서\n• AB와 EF: 평행\n• AB와 BC: 한 점에서 만남\n• AB와 DH: 꼬인 위치 (만나지 않고 평행도 아님)',
    },
    followUps: [
      { q: '꼬인 위치에 있는 두 직선을 한 평면 위에 옮길 수 있나요?', a: '아닙니다! 꼬인 위치는 같은 평면 위에 놓을 수 없는 관계입니다.' },
      { q: '평면에서 꼬인 위치가 없는 이유는?', a: '평면은 2차원이라 모든 직선이 같은 평면 위에 있습니다. 꼬인 위치는 3차원(공간)에서만 가능합니다.' },
    ],
  },
  {
    id: 'S5',
    label: '시나리오 5',
    emoji: '🔭',
    category: '동위각·엇각 활용',
    learnStepId: 'GA05',
    question: 'l∥m이고 ∠a=65°일 때, ∠b(엇각)의 크기는?',
    correctAnswer: '∠b=65° (평행선에서 엇각은 같다)',
    wrongAnswer: '∠b=115° (180°-65°로 계산)',
    misconceptionCode: 'GE05',
    note: '엇각을 보각(합이 180°)으로 혼동',
    analysis: {
      diagnosis: '학생이 ∠b=115°로 계산했습니다. 180°-65°는 보각을 구하는 방법인데, 엇각에 잘못 적용했습니다.',
      rootCause: '엇각은 크기가 같습니다(∠a=∠b). 보각(합이 180°)은 동측 내각에서 적용되는 개념입니다.',
      correction: '평행선에서:\n• 엇각: ∠a = ∠b (크기 같음)\n• 동위각: ∠a = ∠c (크기 같음)\n• 동측 내각: ∠a + ∠d = 180°\n\nl∥m, ∠a=65° → ∠b(엇각)=65° ✓',
      example: 'l∥m, ∠a=70°\n→ 엇각 ∠b=70°\n→ 동측 내각 ∠c=180°-70°=110°',
    },
    followUps: [
      { q: '동위각과 엇각 중 어느 것이 같은지 헷갈려요', a: '둘 다 같습니다! 평행선에서 동위각도 같고 엇각도 같습니다. 합이 180°가 되는 건 동측 내각입니다.' },
      { q: '∠a=65°일 때 동측 내각은?', a: '180°-65°=115°. 동측 내각(같은 쪽 내각)의 합이 180°이므로 다른 각은 115°입니다.' },
    ],
  },
]

// ── 서브 컴포넌트 ────────────────────────────────────────────────────────────
function FragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const visible = fragments.slice(0, 2)
  if (visible.length === 0) return null
  return (
    <div className="card border border-sky-200 bg-sky-50 mb-4">
      <p className="text-xs font-bold text-sky-700 mb-2">⚡ AI 예상 취약 개념</p>
      <div className="space-y-1.5">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color}`}>
              <span>{f.fragment.emoji}</span>
              <span className={`text-xs font-semibold flex-1 ${cfg.color}`}>{f.fragment.title}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function FollowUpCard({ followUps }: { followUps: TutorScenario['followUps'] }) {
  const [openIdx, setOpenIdx] = useState<number | null>(null)
  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs font-bold text-sky-600 mb-1">💬 추가 질문</p>
      {followUps.map((fu, i) => (
        <div key={i} className="border border-sky-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setOpenIdx(openIdx === i ? null : i)}
            className="w-full flex items-center justify-between px-3 py-2 bg-sky-50 hover:bg-sky-100 text-left transition-colors"
          >
            <span className="text-xs text-slate-700 font-medium flex-1">{fu.q}</span>
            {openIdx === i
              ? <ChevronUp size={13} className="text-slate-400 flex-shrink-0" />
              : <ChevronDown size={13} className="text-slate-400 flex-shrink-0" />}
          </button>
          {openIdx === i && (
            <div className="px-3 py-2 bg-white">
              <p className="text-xs text-sky-700 font-semibold whitespace-pre-line">{fu.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
export default function G1_1TutorPage() {
  const [selectedId, setSelectedId] = useState<string>('S1')
  const [step, setStep] = useState<'question' | 'wrong' | 'analysis' | 'correct'>('question')
  const [showNote, setShowNote] = useState(false)

  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const scenario = DEMO_SCENARIOS.find(s => s.id === selectedId)!

  const reset = () => { setStep('question'); setShowNote(false) }
  const handleSelect = (id: string) => { setSelectedId(id); reset() }

  return (
    <div className="p-4 space-y-4">
      <FragmentPanel fragments={predictedFragments} />

      {/* 헤더 */}
      <div className="card bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
            <Brain size={20} className="text-sky-600" />
          </div>
          <div>
            <h2 className="text-base font-black text-slate-800">AI 튜터 — 기본 도형</h2>
            <p className="text-xs text-slate-500">G1-1 오개념 분석 · 맞춤 피드백</p>
          </div>
        </div>
      </div>

      {/* 시나리오 선택 */}
      <div>
        <p className="text-xs font-bold text-slate-500 mb-2 px-1">📋 학습 시나리오 선택</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {DEMO_SCENARIOS.map(s => (
            <button key={s.id} onClick={() => handleSelect(s.id)}
              className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
                selectedId === s.id
                  ? 'bg-sky-500 text-white border-sky-500 shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-sky-50 hover:border-sky-200'
              }`}>
              {s.emoji} {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* 시나리오 카드 */}
      <div className="card border-2 border-sky-200">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs px-2 py-0.5 bg-sky-100 text-sky-700 rounded-full font-semibold">
            {scenario.category}
          </span>
          <span className="text-xs text-slate-400">{scenario.learnStepId}</span>
        </div>

        {step === 'question' && (
          <div className="space-y-3">
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <p className="text-xs font-bold text-slate-500 mb-1 flex items-center gap-1">
                <MessageCircle size={11} /> 학생 질문
              </p>
              <p className="text-sm font-semibold text-slate-800">{scenario.question}</p>
            </div>
            <p className="text-xs text-slate-500 text-center">학생의 답을 먼저 예상해보세요.</p>
            <button onClick={() => setStep('wrong')}
              className="w-full py-2.5 bg-sky-500 hover:bg-sky-600 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
              <ChevronRight size={15} /> 학생 답 확인
            </button>
          </div>
        )}

        {step === 'wrong' && (
          <div className="space-y-3">
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="text-xs font-bold text-slate-500 mb-1">📝 문제</p>
              <p className="text-sm text-slate-700">{scenario.question}</p>
            </div>
            <div className="bg-red-50 rounded-xl p-3 border border-red-200">
              <p className="text-xs font-bold text-red-500 mb-1 flex items-center gap-1">
                <XCircle size={11} /> 학생 오답
              </p>
              <p className="text-sm font-bold text-red-700">{scenario.wrongAnswer}</p>
            </div>
            {scenario.note && (
              <button
                onClick={() => setShowNote(!showNote)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-orange-50 border border-orange-200 text-left"
              >
                <AlertTriangle size={12} className="text-orange-500 flex-shrink-0" />
                <span className="text-xs text-orange-700 font-semibold flex-1">{scenario.note}</span>
                {showNote ? <ChevronUp size={12} className="text-orange-400" /> : <ChevronDown size={12} className="text-orange-400" />}
              </button>
            )}
            {showNote && (
              <div className="px-3 py-2 bg-orange-50 rounded-xl border border-orange-100">
                <p className="text-xs text-orange-600 whitespace-pre-line">{scenario.note}</p>
              </div>
            )}
            <button onClick={() => setStep('analysis')}
              className="w-full py-2.5 bg-sky-500 hover:bg-sky-600 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
              <Brain size={15} /> AI 분석 보기
            </button>
          </div>
        )}

        {step === 'analysis' && (
          <div className="space-y-3">
            {/* 진단 */}
            <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
              <p className="text-xs font-bold text-blue-600 mb-1 flex items-center gap-1">
                <Target size={11} /> 오류 진단
              </p>
              <p className="text-xs text-slate-700">{scenario.analysis.diagnosis}</p>
            </div>
            {/* 근본 원인 */}
            <div className="bg-violet-50 rounded-xl p-3 border border-violet-200">
              <p className="text-xs font-bold text-violet-600 mb-1 flex items-center gap-1">
                <AlertTriangle size={11} /> 근본 원인
              </p>
              <p className="text-xs text-slate-700">{scenario.analysis.rootCause}</p>
            </div>
            {/* 교정 */}
            <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200">
              <p className="text-xs font-bold text-emerald-600 mb-1 flex items-center gap-1">
                <BookOpen size={11} /> 개념 교정
              </p>
              <p className="text-xs text-slate-700 whitespace-pre-line">{scenario.analysis.correction}</p>
            </div>
            {/* 예시 */}
            <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
              <p className="text-xs font-bold text-amber-600 mb-1 flex items-center gap-1">
                <Sparkles size={11} /> 유사 예시
              </p>
              <p className="text-xs text-slate-700 whitespace-pre-line">{scenario.analysis.example}</p>
            </div>
            <button onClick={() => setStep('correct')}
              className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
              <CheckCircle size={15} /> 정답 확인
            </button>
          </div>
        )}

        {step === 'correct' && (
          <div className="space-y-3">
            <div className="bg-emerald-50 rounded-xl p-4 border-2 border-emerald-300">
              <p className="text-xs font-bold text-emerald-600 mb-2 flex items-center gap-1">
                <CheckCircle size={13} /> 정답
              </p>
              <p className="text-sm font-bold text-emerald-800 whitespace-pre-line">{scenario.correctAnswer}</p>
            </div>
            <FollowUpCard followUps={scenario.followUps} />
            <button onClick={reset}
              className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
              <RotateCcw size={14} /> 다시 풀기
            </button>
          </div>
        )}
      </div>

      {/* 단계 표시 */}
      <div className="flex items-center justify-center gap-2">
        {(['question', 'wrong', 'analysis', 'correct'] as const).map((s, i) => (
          <div key={s} className={`w-2 h-2 rounded-full transition-all ${step === s ? 'bg-sky-500 w-4' : 'bg-slate-200'}`} />
        ))}
      </div>

      {/* 핵심 오개념 요약 */}
      <div className="card border border-sky-100 bg-sky-50">
        <p className="text-xs font-bold text-sky-700 mb-2 flex items-center gap-1">
          <Brain size={12} /> 기본 도형 핵심 오개념
        </p>
        <ul className="space-y-1.5">
          {[
            { code: 'GE01', text: '직선·반직선·선분 구분 혼동' },
            { code: 'GE02', text: '각도 범위별 각의 종류 혼동 (예각/둔각)' },
            { code: 'GE03', text: '동위각과 동측 내각 성질 혼동' },
            { code: 'GE04', text: '꼬인 위치 개념 누락 (평면→공간 확장)' },
            { code: 'GE05', text: '엇각을 보각으로 착각' },
          ].map(item => (
            <li key={item.code} className="flex items-start gap-2">
              <span className="text-xs px-1.5 py-0.5 bg-sky-200 text-sky-700 rounded font-mono font-bold flex-shrink-0">{item.code}</span>
              <span className="text-xs text-slate-600">{item.text}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* 관련 학습 단계 */}
      <div className="card border border-slate-200">
        <p className="text-xs font-bold text-slate-600 mb-2">📚 관련 학습 단계</p>
        <div className="grid grid-cols-1 gap-1.5">
          {[
            { id: 'GA01', title: '점·선·면의 개념' },
            { id: 'GA02', title: '각의 종류와 측정' },
            { id: 'GA03', title: '평행선과 교선' },
            { id: 'GA04', title: '두 직선의 위치 관계' },
            { id: 'GA05', title: '동위각·엇각' },
          ].map(step => (
            <div key={step.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
              <span className="text-xs font-mono text-sky-600 font-bold">{step.id}</span>
              <span className="text-xs text-slate-600">{step.title}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
